import { Component, forwardRef, Inject, Input, NgZone, ChangeDetectionStrategy } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { fromEvent } from 'rxjs';

import { BaseEditor } from '../../base/base-editor';
import { NGX_MONACO_EDITOR_CONFIG, NgxMonacoEditorConfig } from '../../config/config';
import { NgxEditorModel } from '../../types/types';

declare var monaco: any;

@Component({
  selector: 'app-monaco-editor',
  templateUrl: './monaco-editor.component.html',
  styleUrls: ['./monaco-editor.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MonacoEditorComponent),
      multi: true
    }
  ],
})
export class MonacoEditorComponent extends BaseEditor implements ControlValueAccessor {
  /**
   * @see {monaco.editor.IStandaloneCodeEditor}
   */
  editor: any;
  private _value: string = '';

  propagateChange = (_: any) => { };
  onTouched = () => { };

  @Input('model')
  set model(model: NgxEditorModel) {
    this.options.model = model;
    if (this.editor) {
      this.editor.dispose();
      this.initMonaco(this.options);
    }
  }

  constructor(private zone: NgZone, @Inject(NGX_MONACO_EDITOR_CONFIG) private editorConfig: NgxMonacoEditorConfig) {
    super(editorConfig);
    if(editorConfig && editorConfig.baseUrl) {
      let rootPath = (window as any).gspframeworkService.common.getBasePath.get();
      if(rootPath && !editorConfig.baseUrl.startsWith(rootPath)) {
        editorConfig.baseUrl = rootPath + editorConfig.baseUrl;
      }
    }
  }

  writeValue(value: any): void {
    this._value = value || '';
    // Fix for value change while dispose in process.
    setTimeout(() => {
      if (this.editor && !this.options.model) {
        this.editor.setValue(this._value);
      }
    });
  }

  formatDocument() {
    setTimeout(() => {
      if (this.editor) {
        this.editor.getAction("editor.action.formatDocument").run();
      }
    }, 0);
  }

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  protected initMonaco(options: any): void {

    const hasModel = !!options.model;

    if (hasModel) {
      const model = monaco.editor.getModel(options.model.uri || '');
      if (model) {
        options.model = model;
        options.model.setValue(this._value);
      } else {
        options.model = monaco.editor.createModel(options.model.value, options.model.language, options.model.uri);
      }
    }

    this.editor = monaco.editor.create(this._editorContainer.nativeElement, options);

    if (!hasModel) {
      this.editor.setValue(this._value);
    }

    this.editor.onDidChangeModelContent((e: any) => {
      const value = this.editor.getValue();
      this.propagateChange(value);
      // value is not propagated to parent when executing outside zone.
      this.zone.run(() => this._value = value);
    });

    // this.editor.onDidBlurEditorWidget(() => {
    //   this.onTouched();
    // });

    // refresh layout on resize event.
    if (this._windowResizeSubscription) {
      this._windowResizeSubscription.unsubscribe();
    }
    this._windowResizeSubscription = fromEvent(window, 'resize').subscribe(() => this.editor.layout());
    this.onInit.emit(this.editor);
  }

}
